/* eslint-disable react/require-default-props */
import React, { useState, useRef, useMemo, useCallback } from 'react';
import ClickAwayListener from 'react-click-away-listener';
import { Popup } from '@progress/kendo-react-popup';
import { translate } from '@utils/i18n';
import { Button } from '@ui';
import './TreeItemActionsStyles.scss';
const namespace = 'components.tree_item_actions';
export default function TreeItemActions({ item, components, onToggleActive, isTogglingActive, onEdit, onMove, onMoveTo, onMakeMainComponent, supportsSubcomponents = false }) {
    const [open, setOpen] = useState(false);
    const [showMoveToSubmenu, setShowMoveToSubmenu] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const anchor = useRef(null);
    const moveToRef = useRef(null);
    // Utility function to flatten component tree
    const flattenComponents = useCallback((componentList) => {
        const flattened = [];
        componentList.forEach((component) => {
            flattened.push(component);
            if (component.children && component.children.length > 0) {
                flattened.push(...flattenComponents(component.children));
            }
        });
        return flattened;
    }, []);
    // Recursively find component by ID in the entire component tree
    const findComponentById = (componentList, targetId) => {
        // First check direct match
        const directMatch = componentList.find((component) => Number(component.id) === Number(targetId));
        if (directMatch)
            return directMatch;
        // Then search in children recursively
        const foundInChildren = componentList
            .filter((component) => component.children && component.children.length > 0)
            .map((component) => findComponentById(component.children, targetId))
            .find((result) => result !== undefined);
        return foundInChildren;
    };
    const currentComponent = findComponentById(components, Number(item.id));
    const handleClose = () => {
        setOpen(false);
        setShowMoveToSubmenu(false);
        setSearchTerm('');
    };
    const handleClickAway = (e) => {
        // Check if the click target is inside the submenu popup
        const target = e.target;
        const submenu = document.querySelector('.tree-item-actions__submenu');
        // Don't close if clicking inside the submenu or if submenu is showing
        if (showMoveToSubmenu && submenu && submenu.contains(target)) {
            return;
        }
        handleClose();
    };
    const handleEdit = (e) => {
        e.stopPropagation();
        if (onEdit) {
            onEdit(Number(item.id));
        }
        handleClose();
    };
    const handleToggleActive = (e) => {
        e.stopPropagation();
        onToggleActive();
        handleClose();
    };
    const handleMoveUp = (e) => {
        e.stopPropagation();
        if (onMove) {
            onMove(Number(item.id), 'up');
        }
        handleClose();
    };
    const handleMoveDown = (e) => {
        e.stopPropagation();
        if (onMove) {
            onMove(Number(item.id), 'down');
        }
        handleClose();
    };
    const handleMoveToComponent = (e) => {
        e.stopPropagation();
        setShowMoveToSubmenu(true);
    };
    const handleMakeMainComponent = (e) => {
        e.stopPropagation();
        if (onMakeMainComponent) {
            onMakeMainComponent(Number(item.id));
        }
        handleClose();
    };
    const handleMoveTo = (newParentId) => (e) => {
        e.stopPropagation();
        if (onMoveTo) {
            onMoveTo(Number(item.id), newParentId);
        }
        handleClose();
    };
    // Get available components to move to (same attachable, excluding self and descendants)
    const availableComponents = useMemo(() => {
        if (!currentComponent)
            return [];
        const allComponents = flattenComponents(components);
        const sameAttachableComponents = allComponents.filter((c) => c.attachableId === currentComponent.attachableId && c.id !== currentComponent.id);
        // Filter out descendants to prevent circular references
        const filteredByDescendants = sameAttachableComponents.filter((c) => {
            // Don't allow moving to own descendants
            const isDescendant = (checkComponent, ancestorId) => {
                if (checkComponent.parentId === ancestorId)
                    return true;
                const parent = allComponents.find((p) => p.id === checkComponent.parentId);
                return parent ? isDescendant(parent, ancestorId) : false;
            };
            return !isDescendant(c, currentComponent.id);
        });
        // Apply search filter
        const filteredComponents = searchTerm
            ? filteredByDescendants.filter((c) => c.name.toLowerCase().includes(searchTerm.toLowerCase()))
            : filteredByDescendants;
        // Convert to options with indentation level
        return filteredComponents.map((c) => ({
            id: c.id,
            name: c.name,
            level: c.depth
        }));
    }, [currentComponent, components, flattenComponents, searchTerm]);
    const canMoveUp = useMemo(() => {
        if (!currentComponent)
            return false;
        const allComponents = flattenComponents(components);
        // Find siblings with same parent
        const siblings = allComponents.filter((c) => c.attachableId === currentComponent.attachableId && c.parentId === currentComponent.parentId);
        // Sort by position
        siblings.sort((a, b) => a.position - b.position);
        // Can move up if not first in the list
        const currentIndex = siblings.findIndex((c) => c.id === currentComponent.id);
        return currentIndex > 0;
    }, [currentComponent, components, flattenComponents]);
    const canMoveDown = useMemo(() => {
        if (!currentComponent)
            return false;
        const allComponents = flattenComponents(components);
        // Find siblings with same parent
        const siblings = allComponents.filter((c) => c.attachableId === currentComponent.attachableId && c.parentId === currentComponent.parentId);
        // Sort by position
        siblings.sort((a, b) => a.position - b.position);
        // Can move down if not last in the list
        const currentIndex = siblings.findIndex((c) => c.id === currentComponent.id);
        return currentIndex < siblings.length - 1;
    }, [currentComponent, components, flattenComponents]);
    const handleTriggerClick = (e) => {
        e.stopPropagation();
        setOpen(!open);
    };
    const handlePopupClick = (e) => {
        e.stopPropagation();
    };
    return (React.createElement("div", { className: "tree-item-actions", onClick: handlePopupClick, role: "button", tabIndex: 0, onKeyDown: (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handlePopupClick(e);
            }
        } },
        React.createElement(Button, { ref: anchor, onClick: handleTriggerClick, className: "tree-item-actions__trigger", disabled: isTogglingActive, title: translate('more_actions', { namespace }), type: "button" }, isTogglingActive ? (React.createElement("span", { className: "loading-spinner loading-spinner--small" })) : (React.createElement("i", { className: "fa-light fa-ellipsis" }))),
        React.createElement(Popup, { anchor: anchor.current, show: open, onClose: handleClose },
            React.createElement(ClickAwayListener, { onClickAway: handleClickAway },
                React.createElement("div", { className: "tree-item-actions__popup", onClick: handlePopupClick, role: "dialog", "aria-label": translate('component_actions_menu', { namespace }) },
                    React.createElement("ul", { role: "presentation", className: "tree-item-actions__menu" },
                        canMoveUp && (React.createElement("li", { role: "presentation", className: "tree-item-actions__menu-item" },
                            React.createElement(Button, { onClick: handleMoveUp, className: "tree-item-actions__menu-button" }, translate('move_up', { namespace })))),
                        canMoveDown && (React.createElement("li", { role: "presentation", className: "tree-item-actions__menu-item" },
                            React.createElement(Button, { onClick: handleMoveDown, className: "tree-item-actions__menu-button" }, translate('move_down', { namespace })))),
                        supportsSubcomponents && availableComponents.length > 0 && (React.createElement("li", { role: "presentation", className: "tree-item-actions__menu-item tree-item-actions__menu-item--submenu", ref: moveToRef },
                            React.createElement(Button, { onClick: handleMoveToComponent, className: "tree-item-actions__menu-button tree-item-actions__menu-button--has-arrow" },
                                translate('move_to_component', { namespace }),
                                React.createElement("i", { className: "fa-light fa-chevron-right" })))),
                        (canMoveUp || canMoveDown || availableComponents.length > 0) && (React.createElement("li", { className: "tree-item-actions__separator" })),
                        (currentComponent === null || currentComponent === void 0 ? void 0 : currentComponent.parentId) && (React.createElement("li", { role: "presentation", className: "tree-item-actions__menu-item" },
                            React.createElement(Button, { onClick: handleMakeMainComponent, className: "tree-item-actions__menu-button" }, translate('turn_into_main_component', { namespace })))),
                        React.createElement("li", { className: "tree-item-actions__separator" }),
                        React.createElement("li", { role: "presentation", className: "tree-item-actions__menu-item" },
                            React.createElement(Button, { onClick: handleEdit, className: "tree-item-actions__menu-button tree-item-actions__menu-button--with-icon" },
                                React.createElement("i", { className: "fa-light fa-pencil" }),
                                translate('edit', { namespace }))),
                        React.createElement("li", { role: "presentation", className: "tree-item-actions__menu-item" },
                            React.createElement(Button, { onClick: handleToggleActive, className: `tree-item-actions__menu-button tree-item-actions__menu-button--with-icon ${(currentComponent === null || currentComponent === void 0 ? void 0 : currentComponent.active) ? 'tree-item-actions__menu-button--danger' : ''}` },
                                React.createElement("i", { className: (currentComponent === null || currentComponent === void 0 ? void 0 : currentComponent.active) ? 'fa-light fa-ban' : 'fa-light fa-check-circle' }),
                                (currentComponent === null || currentComponent === void 0 ? void 0 : currentComponent.active)
                                    ? translate('deactivate', { namespace })
                                    : translate('activate', { namespace }))))))),
        showMoveToSubmenu && moveToRef.current && (React.createElement(Popup, { anchor: moveToRef.current, show: showMoveToSubmenu, anchorAlign: { horizontal: 'right', vertical: 'top' }, popupAlign: { horizontal: 'left', vertical: 'top' } },
            React.createElement("div", { className: "tree-item-actions__submenu", onClick: handlePopupClick, role: "dialog", "aria-label": translate('move_component_submenu', { namespace }) },
                React.createElement("div", { className: "tree-item-actions__search" },
                    React.createElement("input", { type: "text", placeholder: translate('search_components', { namespace }), value: searchTerm, onChange: (e) => {
                            e.stopPropagation();
                            setSearchTerm(e.target.value);
                        }, className: "tree-item-actions__search-input" }),
                    React.createElement("i", { className: "fa-light fa-magnifying-glass tree-item-actions__search-icon" })),
                React.createElement("ul", { className: "tree-item-actions__component-list" }, availableComponents.length > 0 ? (availableComponents.map((component) => (React.createElement("li", { key: component.id, className: "tree-item-actions__component-item" },
                    React.createElement(Button, { onClick: handleMoveTo(component.id), className: "tree-item-actions__component-button", style: { paddingLeft: `${component.level * 20 + 12}px` } }, component.name))))) : (React.createElement("li", { className: "tree-item-actions__no-components" }, searchTerm
                    ? translate('no_components_found', { namespace })
                    : translate('no_available_components', { namespace })))))))));
}
